import { useContext, useEffect, useMemo, useState } from 'react'
import appContext from 'context/appContext'
import useApi from 'hooks/useApi'
import { useNavigate, useParams } from 'react-router-dom'
import { getFormattedDate } from 'utils'
import { PDP_GENERATOR_API_HEADERS, unknown } from 'utils/constants'
import BackNavigationButton from 'components/BackNavigationButton/BackNavigationButton'
import Banner from 'components/Banner'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import { statusChecker } from 'components/StatusMessage/Status'
import { WppTypography } from '@wppopen/components-library-react'
import { MenuItems } from 'components/MenuListPopUp/MenuListPopUp'
import TabsCarousel from 'components/TabsCarousel/TabsCarousel'
import { SwiperSlide } from 'swiper/react'
import { CircleFlag } from 'react-circle-flags'
import RenderEmptyProjectRegion from 'components/RenderEmptyProjectRegion/RenderEmptyProjectRegion'
import RenderSelectedProjectRegion from 'components/RenderSelectedProjectRegion/RenderSelectedProjectRegion'
import { AddAndEditProjectRegion, CreateAndEditNewProject, GenericModal } from 'components/Modals'
import ProjectPageContext from 'context/projectContext/projectContext'
import usePermissions from 'hooks/usePermissions'
import useInitialiser from 'hooks/useInitialiser'
import PageHeader from 'components/PageHeader/PageHeader'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import clsx from 'clsx'
import { defaultFieldsSettings, type OnSubmitGenericModalProps } from 'components/Modals/GenericModal/helper'
import useToast from 'hooks/useToast'
import type {
  ProjectsArchiveCreateData,
  ProjectsCreateBody,
  ProjectsRegionsCreateData,
  ProjectsRegionsCreatePayload,
} from '@contracts'
import type { FEApiError } from 'utils/errorHandling'
import navigationHeaderStyles from '../Settings/NavigationHeader.module.scss'
import styles from './ProjectView.module.scss'

function ProjectViewV2() {
  const { project_id } = useParams()
  const {
    singleProjectApiDetails: getSingleProjectApiMethods,
    activeRegion,
    setActiveRegion,
    updateSingleProject,
    brandDetails,
    clientDetails,
    toastDetailsContext,
    setToastDetailsContext,
    activeRegionData,
  } = useContext(ProjectPageContext)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isRegionModalOpen, setIsRegionModalOpen] = useState<boolean>(false)
  const { newProjectIDForBanner } = useContext(appContext)
  const { toastDetails, setToastDetails } = useToast(true)
  const [isOneRegionLoading, setIsOneRegionLoading] = useState<boolean>(false)
  const { isInitialising } = useInitialiser({ subscribedApis: [getSingleProjectApiMethods] })

  const navigate = useNavigate()

  // get project details
  const {
    fetchData: getSingleProject,
    data: getSingleProjectData,
    isLoading: isGetSingleProjectLoading,
    url: getSingleProjectUrl,
    error: getSingleProjectError,
  } = getSingleProjectApiMethods

  // archive project
  const {
    fetchData: postArchiveProject,
    url: postArchiveProjectUrl,
    isLoading: isPostArchiveProjectLoading,
  } = useApi<ProjectsArchiveCreateData>(`v2/projects/${project_id}/archive`)
  // add region to project
  const {
    fetchData: postProjectRegion,
    url: postProjectRegionUrl,
    isLoading: isPostProjectRegionLoading,
  } = useApi<ProjectsRegionsCreateData>(`v2/projects/${project_id}/regions`)

  const { clientLevelPermissions } = usePermissions({
    clientId: clientDetails?.id,
    permissionSubset: [
      'PDP_APP_GET_V2_PROJECTS_BY',
      'PDP_APP_PATCH_V2_PROJECTS_BY',
      'PDP_APP_POST_V2_PROJECTS_BY_REGIONS',
      'PDP_APP_POST_V2_PROJECTS_BY_ARCHIVE',
    ],
  })

  const [canGetProject, canPatchProject, canPostProjectRegion, canPostArchiveProject] = clientLevelPermissions

  // ============= BANNER ============= //
  useEffect(() => {
    if (!!newProjectIDForBanner && newProjectIDForBanner === project_id) {
      setToastDetails({
        message: 'Project created successfully',
        type: 'success',
      })
    }
  }, [newProjectIDForBanner, project_id])
  // ============= END BANNER ============= //

  // ============= HANDLERS ============= //
  const handleEditProject = async (data: ProjectsCreateBody) => {
    const project_name = data.project_name
    if (project_name === getSingleProjectData?.project_name) throw new Error('No changes detected')

    try {
      const response = await getSingleProject(getSingleProjectUrl, {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: data.client_id as string },
      })
      const _id = response?._id
      if (!_id) throw new Error('No project ID returned from the API')
      if (updateSingleProject) await updateSingleProject()
      setToastDetails({
        message: 'Project updated successfully',
        type: 'success',
      })
      setIsEditModalOpen(false)
    } catch (err) {
      throw err
    }
  }

  const handleDeleteProject = async (data: OnSubmitGenericModalProps) => {
    try {
      await postArchiveProject(postArchiveProjectUrl, {
        method: 'POST',
        body: JSON.stringify({
          update_info: data,
        }),
      })

      navigate(`/projects?deleted_project=${getSingleProjectData?.project_name}`)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const handleAddRegion = async (data: ProjectsRegionsCreatePayload) => {
    try {
      const response = await postProjectRegion(postProjectRegionUrl, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      setActiveRegion(response._id!)
      if (updateSingleProject) await updateSingleProject()
      setToastDetails({
        message: 'Region added successfully',
        type: 'success',
      })
      setIsRegionModalOpen(false)
    } catch (err) {
      throw err
    }
  }

  // ============= END HANDLERS ============= //

  const menuItems: MenuItems = useMemo(() => {
    const returnMenuItems: MenuItems = []
    if (canPatchProject) returnMenuItems.push({ label: 'Edit project', onClick: () => setIsEditModalOpen(true) })
    // pushed here to maintain order of menu items
    returnMenuItems.push({ label: 'View import logs', onClick: () => navigate(`/project/${project_id}/logs`) })
    if (canPostProjectRegion) returnMenuItems.push({ label: 'Add region', onClick: () => setIsRegionModalOpen(true) })
    if (canPostArchiveProject)
      returnMenuItems.push({ label: 'Delete project', onClick: () => setIsDeleteModalOpen(true) })

    return returnMenuItems
  }, [canPatchProject, canPostProjectRegion, canPostArchiveProject])

  if (
    !isInitialising &&
    (!canGetProject ||
      getSingleProjectError?.status === 404 ||
      getSingleProjectError?.status === 500 ||
      (!isGetSingleProjectLoading && !getSingleProjectData && getSingleProjectError))
  )
    return <NotFound404Page />

  const isRegionsEmpty = !getSingleProjectData?.regions || getSingleProjectData?.regions.length === 0

  return (
    <>
      <div className="dark-bg-page ">
        <section className="light-header">
          <div className={navigationHeaderStyles.navigationHeader__container}>
            <BackNavigationButton href="/projects" text="Back to Projects" isBottomMargin={false} />
            <Banner
              shouldBannerShow={!!toastDetails || !!toastDetailsContext}
              text={toastDetails?.message || toastDetailsContext?.message}
              type={toastDetails?.type || toastDetailsContext?.type}
              onBannerClick={toastDetails?.onBannerClick}
              onClose={() => {
                if (!!toastDetails) setToastDetails(undefined)
                if (!!toastDetailsContext) setToastDetailsContext(undefined)
              }}
            />
          </div>

          <PageHeader
            isLoading={isInitialising}
            tags={[statusChecker(getSingleProjectData?.status, true).tag]}
            createdDetails={`Created: ${getFormattedDate(getSingleProjectData?.created_at)} by ${getSingleProjectData?.update_info?.user_name || unknown}`}
            pageTitle={getSingleProjectData?.project_name || ''}
            menuItems={menuItems}
            details={{
              Client: `${clientDetails?.client_name || getSingleProjectData?.client_id} | ${brandDetails?.brand_name || getSingleProjectData?.brand_id}`,
            }}
          />

          <TabsCarousel
            className={styles.regionsTabsSection}
            isActiveButton={getSingleProjectData?.regions && getSingleProjectData?.regions?.length > 1}
            onPlusClick={
              getSingleProjectData?.regions && getSingleProjectData.regions.length >= 1
                ? () => setIsRegionModalOpen(true)
                : undefined
            }
            permissions={{ canPostProjectRegion }}
          >
            {getSingleProjectData?.regions?.map(region => (
              <SwiperSlide
                key={region._id}
                className={clsx(activeRegion === region._id && styles.activeRegion)}
                onClick={() => {
                  if (isOneRegionLoading) return
                  setActiveRegion(region._id)
                }}
                aria-label="region-slider"
                data-testid="region-slider"
              >
                <CircleFlag countryCode={region.region_flag!} height="20" />
                <WppTypography type="s-strong" data-testid="region-name">
                  {region.region_name} ({region?.region_locale?.toUpperCase().replace('_', '-')})
                </WppTypography>
              </SwiperSlide>
            ))}
          </TabsCarousel>
        </section>

        <section className="page-content">
          {!isGetSingleProjectLoading && getSingleProjectData ? (
            <>
              {isRegionsEmpty ? (
                <RenderEmptyProjectRegion
                  onClick={() => setIsRegionModalOpen(true)}
                  permissions={{ canPostProjectRegion }}
                  isLoading={isInitialising}
                />
              ) : (
                <RenderSelectedProjectRegion
                  singleProjectData={getSingleProjectData!}
                  activeRegionData={activeRegionData!}
                  activeRegionId={activeRegion}
                  setToastDetails={setToastDetails}
                  refreshSingleProject={updateSingleProject}
                  setIsOneRegionLoading={setIsOneRegionLoading}
                />
              )}
            </>
          ) : (
            <LoadingSpinner />
          )}
        </section>
      </div>

      {isEditModalOpen && (
        <CreateAndEditNewProject
          isModalOpen={isEditModalOpen}
          projectData={getSingleProjectData!}
          isApiLoading={isGetSingleProjectLoading}
          onCancel={() => setIsEditModalOpen(false)}
          onSubmit={handleEditProject}
        />
      )}

      {canPostArchiveProject && isDeleteModalOpen && (
        <GenericModal
          onCancel={() => setIsDeleteModalOpen(false)}
          onSubmit={handleDeleteProject}
          modalTitle="Delete project"
          submitButtonText="Delete"
          shouldModalOpen={isDeleteModalOpen}
          isApiLoading={isPostArchiveProjectLoading}
          fieldsConfig={{
            ...defaultFieldsSettings,
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to delete this project?
            <br />
            After you delete a project this cannot be undone.
          </WppTypography>
        </GenericModal>
      )}

      {canPostProjectRegion && isRegionModalOpen && (
        <AddAndEditProjectRegion
          isModalOpen={isRegionModalOpen}
          isApiLoading={isPostProjectRegionLoading}
          onCancel={() => setIsRegionModalOpen(false)}
          onSubmit={handleAddRegion}
        />
      )}
    </>
  )
}
export default ProjectViewV2
